import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const LoginPage = ({ onLogin, authMessage }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setError('');
    setIsLoading(true);
    try {
      await onLogin(username, password);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 font-sans" style={{ backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[500px] h-[500px] bg-indigo-500/[0.04] rounded-full blur-[120px]" />
      </div>

      <div className="relative w-full max-w-sm rounded-2xl shadow-2xl shadow-black/40 p-8" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-primary)' }}>
        <div className="mb-8 text-center">
          <h1 className="text-xl font-bold tracking-tight mb-1.5">
            <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">TCM</span>
            <span style={{ color: 'var(--text-muted)' }} className="font-normal ml-1">Search</span>
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-faint)' }}>Sign in to your account</p>
        </div>
        
        {authMessage && (
          <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400/90 text-sm text-center">
            {authMessage}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400/90 text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium ml-0.5" style={{ color: 'var(--text-faint)' }}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200"
              style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
              placeholder="Enter your username"
              disabled={isLoading}
              autoFocus
              autoComplete="username"
            />
          </div>
          
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium ml-0.5" style={{ color: 'var(--text-faint)' }}>Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl pl-3.5 pr-10 py-2.5 text-sm focus:outline-none focus:shadow-[0_0_0_1px_rgba(99,102,241,0.3)] transition-all duration-200"
                style={{ backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-secondary)', color: 'var(--text-primary)' }}
                placeholder="Enter your password"
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button 
                type="button" 
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-150 p-1"
                style={{ color: 'var(--text-faint)' }}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            className="w-full mt-3 bg-indigo-500 text-[#f4f4f5] font-medium text-sm py-2.5 rounded-xl hover:bg-indigo-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20"
            disabled={isLoading || !username || !password}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;